import type { Unit } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { exoticCountsText } from "../lib/dataset";
import { priceParts } from "../lib/format";
import type { StatFrame } from "../lib/frame";
import {
  carriedLoadout,
  itemSlotCapacity,
  type LoadoutOption,
  loadoutGroupsFor,
} from "../lib/loadout-index";
import { ArtImage } from "./art-image";
import { Chip } from "./chip";
import { keepPanelOnScreen, PanelAction, PickerPanel, RowFooter } from "./picker-panel";

// The Loadout picker (Issue 46, ADR 0012): the panel a reader equips one hull's Ship Items
// in. It is the Tech picker's sibling in every way that matters — the same panel shell, the
// same row shape (art, the game's own name, its Effect Lines, its Price), the same transient
// open state (ADR 0002: the panel belongs to this visit, what is ticked belongs to the URL) —
// and it differs only where the two axes differ.
//
// Two of those differences are the whole point of the control:
//
//  - The rows are EVERY item the hull can buy, grouped by the game's own shop tabs, not only
//    the 48% that move a published cell. That is the rule the ADR 0009 amendment already
//    settled for research: a control that works on one row and refuses the one beside it
//    teaches the reader that the site is unreliable, not anything about the game.
//  - A Loadout has a cap, so a tick can be REFUSED. It is refused with a reason, in the note
//    the picker's caller renders above the control, never by silently dropping something the
//    reader chose. Every rule behind that — the ladder, the exclusions, the coupling to
//    research — lives in lib/loadout.ts and is reached through lib/loadout-index.ts; nothing
//    here restates one.

/** The Price line: the game's own credits / metal / crystal, then the exotics it costs. */
function ItemPrice({ option }: { option: LoadoutOption }) {
  const exotics = exoticCountsText(option.exoticPrice);
  const parts = [...priceParts(option.price), ...(exotics === "" ? [] : [exotics])];
  if (parts.length === 0) return null;
  return <span>{parts.join(", ")}</span>;
}

/** The rules a tick will apply, in the game's own names — the rung it stands on, what it may
 * not sit beside, and the research that buys it. Says them BEFORE the click, so a refusal or
 * a cascade is never the first the reader hears of them. */
function ItemRules({ option }: { option: LoadoutOption }) {
  const parts = [
    ...(option.needs.length > 0 ? [`Needs ${option.needs.join(", ")}`] : []),
    ...(option.excludes.length > 0 ? [`Not beside ${option.excludes.join(", ")}`] : []),
    ...(option.research.length > 0 ? [`Research: ${option.research.join(", ")}`] : []),
  ];
  if (parts.length === 0) return null;
  return <span className="text-muted-2">{parts.join(" · ")}</span>;
}

function PickerRow({
  option,
  checked,
  onToggle,
}: {
  option: LoadoutOption;
  checked: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <li data-item-id={option.id} data-item-equipped={checked}>
      <label
        className={clsx(
          "flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-surface",
          checked && "bg-surface",
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onToggle(event.target.checked)}
          // Never `outline-none`: in Tailwind v4 it sets --tw-outline-style: none and
          // defeats the focus-visible ring the keyboard path depends on.
          className="mt-[3px] shrink-0 cursor-pointer accent-link focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link"
        />
        <ArtImage
          asset={option.art}
          alt=""
          className={clsx(
            "mt-px h-6 w-6 shrink-0 rounded border border-line-faint object-contain",
            !checked && "opacity-70",
          )}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className={clsx(
                "min-w-0 break-words text-[13.5px]",
                checked ? "text-ink" : "text-ink-3",
              )}
            >
              {option.label}
            </span>
            {option.faction !== undefined && (
              <Chip
                label={option.faction}
                title={`Only the ${option.faction} can buy this`}
                className="shrink-0"
              />
            )}
            {option.level !== undefined && (
              <span
                className="shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-2"
                title="The game levels an item by research alone, so the Tech Set decides this."
              >
                Level {option.level.current} of {option.level.count}
              </span>
            )}
          </span>
          <RowFooter effects={option.effects}>
            <ItemPrice option={option} />
            <ItemRules option={option} />
          </RowFooter>
        </span>
      </label>
    </li>
  );
}

export function LoadoutPicker({
  panelId,
  unit,
  frame,
  note,
  onEquip,
  onUnequip,
  onClearAll,
  onClose,
}: {
  /** The panel's own DOM id, wired to its trigger's aria-controls. */
  panelId: string;
  unit: Unit;
  frame: StatFrame;
  /** What the last change did, said out loud — a refusal with its reason, the rungs a tick
   * brought, the items an exclusion or an untick took off. */
  note?: string;
  onEquip: (id: string) => void;
  onUnequip: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // The control sits third in the toolbar, so its left edge moves with the two triggers
  // beside it and a left-anchored panel can hang past the viewport at some widths. Nudge it
  // back on open — the shell's one rule, shared with the Tech picker.
  useEffect(() => {
    if (panel.current !== null) keepPanelOnScreen(panel.current);
  }, []);

  const capacity = itemSlotCapacity(unit, frame);
  const carried = carriedLoadout(unit, frame);
  const groups = useMemo(() => loadoutGroupsFor(unit, frame), [unit, frame]);

  return (
    <PickerPanel
      panelRef={panel}
      panelId={panelId}
      width="w-[min(30rem,calc(100vw-1.5rem))]"
      summary={`${carried.size} of ${capacity} item ${capacity === 1 ? "slot" : "slots"} filled`}
      actions={
        <>
          <PanelAction onClick={onClearAll}>Take all off</PanelAction>
          <PanelAction onClick={onClose}>Close</PanelAction>
        </>
      }
      {...(note === undefined ? {} : { note })}
    >
      {groups.map((group) => (
        <section key={group.id} aria-label={group.name}>
          <h3 className="sticky top-0 z-10 bg-raised pt-2 pb-1 font-mono text-[11.5px] uppercase tracking-[0.12em] text-muted-2">
            {group.name}
          </h3>
          <ul className="flex flex-col border-l border-line-faint pl-2">
            {group.options.map((option) => (
              <PickerRow
                key={option.id}
                option={option}
                checked={carried.has(option.id)}
                onToggle={(on) => (on ? onEquip(option.id) : onUnequip(option.id))}
              />
            ))}
          </ul>
        </section>
      ))}
    </PickerPanel>
  );
}
